import React, { useState, useEffect } from "react";
import { useCustomerGameRecords } from "../../../hooks/useGameRecords";
import { Pagination } from "../../common/Pagination";
import { UserName } from "./UserName";
import { GameTableName } from "./GameTableName";

interface GameRecordTableProps {
  customerId?: string;
  selectedDate?: string;
  selectedDateFrom?: string;
  selectedDateTo?: string;
  selectedGames?: string[];
  selectedTables?: string[];
}

export const GameRecordTable: React.FC<GameRecordTableProps> = ({
  customerId,
  selectedDate,
  selectedDateFrom,
  selectedDateTo,
  selectedGames = [],
  selectedTables = [],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedDate,
    selectedDateFrom,
    selectedDateTo,
    selectedGames,
    selectedTables,
  ]);

  const getDateFilters = () => {
    if (selectedDate) {
      return {
        dateFrom: selectedDate,
        dateTo: selectedDate,
      };
    } else if (selectedDateFrom && selectedDateTo) {
      return {
        dateFrom: selectedDateFrom,
        dateTo: selectedDateTo,
      };
    }
    return {};
  };

  const filterParams = {
    skip: (currentPage - 1) * itemsPerPage,
    limit: itemsPerPage,
    ...getDateFilters(),
    ...(selectedGames.length > 0 && { gameIds: selectedGames }),
    ...(selectedTables.length > 0 && { gameTableIds: selectedTables }),
  };

  const {
    data: gameRecordsData,
    isLoading,
    isError,
    error,
  } = useCustomerGameRecords(customerId || "", filterParams);

  const gameRecords = gameRecordsData?.gameRecords || [];
  const total = gameRecordsData?.total || 0;
  const totalPages = Math.ceil(total / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const getDateFilterText = () => {
    if (selectedDate) {
      return `Date: ${formatDate(selectedDate)}`;
    } else if (selectedDateFrom && selectedDateTo) {
      return `Date Range: ${formatDate(selectedDateFrom)} - ${formatDate(selectedDateTo)}`;
    }
    return null;
  };

  const hasActiveFilters = Boolean(
    selectedDate ||
      (selectedDateFrom && selectedDateTo) ||
      selectedGames.length > 0 ||
      selectedTables.length > 0,
  );

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-b-lg overflow-visible">
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-400">Loading game records...</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-slate-800 rounded-b-lg overflow-visible">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-400">
            Error loading game records: {error?.message || "Unknown error"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-b-lg">
      {/* Show active filters */}
      {hasActiveFilters && (
        <div className="px-6 py-3 border-b border-slate-700 bg-slate-750">
          <div className="text-sm text-slate-300 flex flex-wrap gap-4">
            <span className="text-slate-400">Active filters:</span>
            {getDateFilterText() && (
              <span className="text-purple-400">{getDateFilterText()}</span>
            )}
            {selectedGames.length > 0 && (
              <span className="text-blue-400">
                {selectedGames.length} game(s) selected
              </span>
            )}
            {selectedTables.length > 0 && (
              <span className="text-green-400">
                {selectedTables.length} table(s) selected
              </span>
            )}
          </div>
        </div>
      )}

      {/* Horizontal scroll container */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead className="bg-slate-700">
            <tr>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[60px]">
                S.N.
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[80px]">
                Shift
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Date
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[80px]">
                Time
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[120px]">
                Game Table
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Bet
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[80px]">
                Win
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[80px]">
                Loss
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Manager
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Dealer
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Watcher
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {gameRecords.map((record, index) => (
              <tr
                key={record.id}
                className="hover:bg-slate-700 transition-colors"
              >
                <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                  {(currentPage - 1) * itemsPerPage + index + 1}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {record.shift}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                  {formatDate(record.createdAt)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                  {formatTime(record.createdAt)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <GameTableName
                    gameTableId={record.gameTableId}
                    fallback="Unknown Table"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-slate-300">
                  {record.betAmount}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-green-400">
                  {record.winAmount}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-red-400">
                  {record.lossAmount}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <UserName
                    userId={record.managerId}
                    fallback="Unknown Manager"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <UserName
                    userId={record.dealerId}
                    fallback="Unknown Dealer"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <UserName
                    userId={record.watcherId}
                    fallback="Unknown Watcher"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {gameRecords.length === 0 && (
        <div className="text-center py-12">
          <div className="text-slate-400">
            {hasActiveFilters
              ? "No game records found with the selected filters"
              : "No game records found"}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-400">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, total)} of {total} records
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};
